<script setup>
import { useI18n } from 'vue-i18n';
import { useStore } from 'dashboard/composables/store';
import { useAlert, useTrack } from 'dashboard/composables';
import { CAMPAIGN_TYPES } from 'shared/constants/campaign.js';
import { CAMPAIGNS_EVENTS } from 'dashboard/helper/AnalyticsHelper/events.js';
import { ref } from 'vue';

import Modal from 'dashboard/components/Modal.vue';
import WhatsAppCsvCampaignForm from 'dashboard/components-next/Campaigns/Pages/CampaignPage/WhatsAppCampaign/WhatsAppCsvCampaignForm.vue';

const emit = defineEmits(['close']);

const store = useStore();
const { t } = useI18n();
const csvFile = ref(null);
const mediaFile = ref(null);
const showModal = ref(true);
const csvErrors = ref(null);

const handleClose = () => {
  showModal.value = false;
  emit('close');
};

async function validateCsvFile(file) {
  if (!file) return undefined;

  csvErrors.value = null;

  try {
    const formData = new FormData();
    formData.append('campaign[csv_file]', file);

    const result = await store.dispatch('campaigns/validateCSV', formData);

    if (result.valid) {
      return true;
    }

    csvErrors.value = result.csv_errors;
    return false;
  } catch (error) {
    const errorMessage =
      error.response?.data?.error ||
      t('CAMPAIGN.WHATSAPP_CSV.CREATE.FORM.ERRORS.VALIDATION_FAILED');
    useAlert(errorMessage);
    return false;
  }
}

async function addCampaign(campaignDetails) {
  try {
    const formData = new FormData();

    let variableMappings = campaignDetails.variable_mappings;

    if (variableMappings && typeof variableMappings !== 'string') {
      variableMappings = JSON.stringify(variableMappings);
    }

    Object.keys(campaignDetails).forEach(key => {
      if (
        key !== 'variable_mappings' &&
        key !== 'audience' &&
        key !== 'use_csv'
      ) {
        formData.append(
          `campaign[${key}]`,
          campaignDetails[key] === null ? '' : campaignDetails[key]
        );
      }
    });

    ['title', 'inbox_id', 'message'].forEach(field => {
      if (!formData.has(`campaign[${field}]`)) {
        const value = campaignDetails[field] || '';
        formData.append(`campaign[${field}]`, value);
      }
    });

    if (variableMappings) {
      formData.append('campaign[variable_mappings]', variableMappings);
    }

    if (csvFile.value) {
      formData.append('campaign[csv_file]', csvFile.value);
    }

    if (mediaFile.value) {
      formData.append('campaign[media_file]', mediaFile.value);
    }

    formData.append('campaign[use_csv]', 'true');

    if (campaignDetails.audience && campaignDetails.audience.length > 0) {
      formData.append('audience', JSON.stringify(campaignDetails.audience));
    }

    await store.dispatch('campaigns/createWithCSV', formData);

    useTrack(CAMPAIGNS_EVENTS.CREATE_CAMPAIGN, {
      type: CAMPAIGN_TYPES.ONE_OFF,
    });
    const successMessage = t(
      'CAMPAIGN.WHATSAPP_CSV.CREATE.API.SUCCESS_MESSAGE'
    );
    useAlert(successMessage);
    handleClose();
  } catch (error) {
    useTrack('ERROR', {
      type: 'CAMPAIGN_CREATION_ERROR',
      message: error?.message || 'Unknown error',
    });

    if (error?.response?.data?.csv_errors) {
      csvErrors.value = error.response.data.csv_errors;
    } else {
      const errorMessage =
        error?.response?.data?.error ||
        t('CAMPAIGN.WHATSAPP_CSV.CREATE.API.ERROR_MESSAGE');
      useAlert(errorMessage);
    }
  }
}

const handleSubmit = campaignDetails => {
  addCampaign(campaignDetails);
};

const handleCsvFileSelected = file => {
  csvFile.value = file;
  csvErrors.value = null;
};

const handleMediaFileSelected = file => {
  mediaFile.value = file;
};
</script>

<template>
  <Modal
    v-model:show="showModal"
    :on-close="handleClose"
    size="medium"
    :close-on-backdrop-click="false"
    data-test-id="whatsapp-campaign-modal"
    @mousedown.stop
  >
    <div class="campaign-modal-content" @click.stop>
      <h3
        class="text-base font-medium text-slate-900 dark:text-slate-50 px-8 py-5 border-b border-slate-100 dark:border-slate-700"
      >
        {{ t(`CAMPAIGN.WHATSAPP_CSV.CREATE.TITLE`) }}
      </h3>
      <div class="px-8 py-6 max-h-[70vh] overflow-y-auto" @click.stop>
        <WhatsAppCsvCampaignForm
          :csv-errors="csvErrors"
          :validate-csv-file="validateCsvFile"
          @submit="handleSubmit"
          @cancel="handleClose"
          @csv-file-selected="handleCsvFileSelected"
          @media-file-selected="handleMediaFileSelected"
        />
      </div>
    </div>
  </Modal>
</template>

<style lang="scss" scoped>
.campaign-modal-content {
  width: 100%;
  max-width: 100%;
  display: flex;
  flex-direction: column;
}
</style>
